import { calculate, calculateSampleSize, getLetter, getWinningVariant } from 'helpers';
import { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_VALUES, FIELD_MAPPING } from '../../constants';

const context = createContext<CalculatorState>({} as CalculatorState);

export const useCalculator = () => useContext(context);

function getExperimentResultLabel(comparisonResult: any, winnerVariant: Variant | undefined) {
    if (!comparisonResult?.evaluation) {
        return '';
    }

    if (winnerVariant) {
        return `${winnerVariant.label} Winner`;
    }

    if (comparisonResult.evaluation === 'CLEARLY_BELOW') {
        return 'Clearly Below Threshold';
    }

    return 'Inconclusive';
}

export function CalculatorProvider({ children }: any) {
    const [comparisonResult, setComparisonResult] = useState<any>({});
    const [confidenceLevel, setConfidenceLevel] = useState<number | undefined>(DEFAULT_VALUES.confidenceLevel);
    const [distributionFunction, setDistributionFunction] = useState<DistributionFunction>(
        DEFAULT_VALUES.distributionFunction
    );
    const [experimentResult, setExperimentResult] = useState('');
    const [goal, setGoal] = useState<Goal>(DEFAULT_VALUES.goal);
    const [name, setName] = useState('');
    const [populationSize, setPopulationSize] = useState<number | undefined>(undefined);
    const [recommendedSampleSize, setRecommendedSampleSize] = useState<any>(undefined);
    const [resultMaxValue, setResultMaxValue] = useState<number | undefined>(undefined);
    const [resultMinMaxValid, setResultMinMaxValid] = useState(false);
    const [resultMinValue, setResultMinValue] = useState<number | undefined>(undefined);
    const [results, setResults] = useState<(Result | null)[]>([]);
    const [sample, setSample] = useState<any>({});
    const [targetMarginOfError, setTargetMarginOfError] = useState<number | undefined>(
        DEFAULT_VALUES.targetMarginOfError
    );
    const [threshold, setThreshold] = useState<number | undefined>(undefined);
    const [variants, setVariants] = useState<Variant[]>([]);
    const [winner, setWinner] = useState(null);
    function addVariant() {
        setVariants(prevVariants => [...prevVariants, createVariant(prevVariants.length)]);
    }

    function createVariant(index: number, data?: Partial<Variant>) {
        return {
            index,
            label: `Variant ${getLetter(index)}`,
            name: '',
            resultType: 'percentage',
            ...data,
        } as Variant;
    }

    function removeVariant(index: number) {
        setVariants(prevVariants => {
            return prevVariants
                .filter((_variant, i) => i !== index)
                .map((variant, index) => ({
                    ...variant,
                    index,
                    label: variant.name || `Variant ${getLetter(index)}`,
                }));
        });
    }

    function updateVariant(index: number, key: string, value: any) {
        setVariants(prevVariants => {
            const label = getLetter(index);
            return prevVariants.map((v, i) =>
                i === index
                    ? {
                          ...v,
                          [key]: value,
                          label: (key === 'name' ? value : v.name) || `Variant ${label}`,
                      }
                    : v
            );
        });
    }

    function resetVariants() {
        setVariants([createVariant(0)]);
        setResults([]);
    }

    function reset() {
        setThreshold(undefined);
        setComparisonResult({});
        setConfidenceLevel(DEFAULT_VALUES.confidenceLevel);
        setDistributionFunction(DEFAULT_VALUES.distributionFunction);
        setGoal(DEFAULT_VALUES.goal);
        setName('');
        setPopulationSize(undefined);
        setResultMaxValue(undefined);
        setResultMinMaxValid(false);
        setResultMinValue(undefined);
        setResults([]);
        setSample({});
        setRecommendedSampleSize(undefined);
        setTargetMarginOfError(DEFAULT_VALUES.targetMarginOfError);
        resetVariants();
    }

    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        const searchParams = new URLSearchParams(hash);
        const newVariants: Variant[] = [];

        if (hash) {
            Object.entries({
                threshold: setThreshold,
                confidenceLevel: setConfidenceLevel,
                distributionFunction: setDistributionFunction,
                goal: setGoal,
                name: setName,
                populationSize: setPopulationSize,
                resultMaxValue: setResultMaxValue,
                resultMinValue: setResultMinValue,
                targetMarginOfError: setTargetMarginOfError,
            }).forEach(([key, func]: any) => {
                const map = FIELD_MAPPING[key];
                const value = searchParams.get(map);
                if (value) {
                    const num = parseFloat(value);
                    func(isNaN(num) ? value : num);
                }
            });

            let i = 0;
            while (searchParams.has(`e${i}`)) {
                const e = searchParams.get(`e${i}`)?.split('|');
                if (e) {
                    const variant = createVariant(i, {
                        label: e[0],
                        sampleSize: parseFloat(e[1]),
                        result: parseFloat(e[2]),
                        resultType: e[3] as NumberFieldType,
                    });
                    newVariants.push(variant);
                }
                i++;
            }
        }

        if (!newVariants.length) {
            newVariants.push(createVariant(0));
        }

        setVariants(newVariants);
    }, []);

    useEffect(() => {
        // Get Sample Size
        const newResultMinMaxValid =
            resultMinValue === undefined ||
            resultMinValue === null ||
            resultMaxValue === undefined ||
            resultMaxValue === null ||
            Number(resultMinValue.toFixed(2)) <= Number(resultMaxValue.toFixed(2));
        setResultMinMaxValid(newResultMinMaxValid);

        const newRecommendedSampleSize = calculateSampleSize({
            confidenceLevel: (confidenceLevel || 0) / 100,
            populationSize,
            resultMaxValue: (newResultMinMaxValid ? resultMaxValue ?? 50 : 50) / 100,
            resultMinValue: (newResultMinMaxValid ? resultMinValue ?? 50 : 50) / 100,
            targetMarginOfError: (targetMarginOfError || 0) / 100,
            threshold: (threshold || 0) / 100,
        });
        setRecommendedSampleSize(newRecommendedSampleSize);

        setSample({
            resultMax: resultMaxValue,
            resultMin: resultMinValue,
            sampleSize: recommendedSampleSize,
            targetMarginOfError,
        });

        // Get results
        const newResults = calculate(
            {
                confidenceLevel: confidenceLevel || 0,
                distributionFunction,
                goal,
                populationSize,
                threshold,
            },
            variants
        );

        const newComparisonResult = getWinningVariant(
            newResults.filter((result: any) => result != null),
            goal,
            threshold
        );

        const range = variants.map((variant, index) => {
            const result = newResults[index];
            if (result) {
                return [result.minValue, result.maxValue];
            } else {
                return [];
            }
        });

        newResults.forEach((result, index) => {
            if (result) {
                if (newComparisonResult.winner?.index === index) {
                    result.evaluation = 'winner';
                } else if (newComparisonResult.isInconclusive) {
                    result.evaluation = 'inconclusive';
                } else if (
                    Object.keys(newComparisonResult).length > 0 &&
                    range[index] !== undefined &&
                    variants.length >= 1
                ) {
                    result.evaluation = 'lost';
                } else {
                    result.evaluation = null;
                }
            }
        });

        const newWinner = newComparisonResult.winner && variants[newComparisonResult.winner.index];
        const newExperimentResult = getExperimentResultLabel(newComparisonResult, newWinner);

        setComparisonResult(newComparisonResult);
        setExperimentResult(newExperimentResult);
        setResults(newResults);
        setWinner(newWinner);

        // Update hash
        const params: any[] = [];
        Object.entries({
            threshold,
            confidenceLevel,
            distributionFunction,
            goal,
            name,
            populationSize,
            resultMaxValue,
            resultMinValue,
            targetMarginOfError,
        }).forEach(([key, value]) => {
            if (value && (!DEFAULT_VALUES[key] || value !== DEFAULT_VALUES[key])) {
                params.push(`${FIELD_MAPPING[key]}=${value}`);
            }
        });

        variants
            .filter(variant => variant.sampleSize && variant.result !== undefined)
            .forEach(variant => {
                params.push(
                    `e${variant.index}=${variant.label}|${variant.sampleSize}|${variant.result}|${variant.resultType}`
                );
            });

        window.history.replaceState({}, '', '#' + params.join('&'));
    }, [
        confidenceLevel,
        distributionFunction,
        goal,
        name,
        populationSize,
        resultMaxValue,
        resultMinValue,
        recommendedSampleSize,
        targetMarginOfError,
        threshold,
        variants,
    ]);

    const state = {
        comparisonResult,
        confidenceLevel,
        distributionFunction,
        experimentResult,
        goal,
        name,
        populationSize,
        recommendedSampleSize,
        resultMaxValue,
        resultMinMaxValid,
        resultMinValue,
        results,
        sample,
        targetMarginOfError,
        threshold,
        title: name || '',
        variants,
        winner,
        addVariant,
        removeVariant,
        reset,
        resetVariants,
        setThreshold: (threshold?: number) => {
            setThreshold(threshold);
        },
        setConfidenceLevel: (confidenceLevel?: number) => {
            setConfidenceLevel(confidenceLevel);
        },
        setDistributionFunction: (distributionFunction: DistributionFunction) => {
            setDistributionFunction(distributionFunction);
        },
        setGoal: (goal: Goal) => {
            setGoal(goal);
        },
        setName: (name: string) => {
            setName(name);
        },
        setPopulationSize: (populationSize?: number) => {
            setPopulationSize(populationSize);
        },
        setResultMaxValue: (resultMaxValue?: number) => {
            setResultMaxValue(resultMaxValue);
        },
        setResultMinValue: (resultMinValue?: number) => {
            setResultMinValue(resultMinValue);
        },
        setTargetMarginOfError: (targetMarginOfError?: number) => {
            setTargetMarginOfError(targetMarginOfError);
        },
        updateVariant,
    };

    return <context.Provider value={state}>{children}</context.Provider>;
}
